import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  RevAlert,
  RevButton,
  RevCard,
  RevDialog,
  RevInput,
} from "@revmatics/catalyst";
import {
  domainRegex,
  hasValidDomain,
  isValidUrl,
} from "../../../helpers/functions/hasValidHelpers";
import { OnboardingFormData } from "../OnboardingPage";
import { useAppDispatch, useAppSelector } from "../../../stores/hooks/hooks";
import {
  fetchBrandList,
  editBrandById,
} from "../../../stores/thunks/brandThunk";
import { fetchPersonasData } from "../../../stores/thunks/personasThunk";
import {
  setInitialPersonaCount,
  setIsWebsiteChanged,
} from "../../../stores/slice/onboardingSlice";

export const Step1_GettingStarted = () => {
  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<OnboardingFormData>();
  const dispatch = useAppDispatch();

  const [isOpenEditWebsite, setOpenEditWebsite] = useState(false);
  const [dialogWebsiteValue, setDialogWebsiteValue] = useState("");
  const { brandList } = useAppSelector((state) => state.brand);
  const websiteUrl = watch("websiteUrl");

  useEffect(() => {
    dispatch(fetchBrandList());
    dispatch(fetchPersonasData())
      .unwrap()
      .then((personas) => {
        dispatch(setInitialPersonaCount(personas.length));
      })
      .catch((error) => {
        console.error("Failed to fetch initial persona count:", error);
      });
  }, [dispatch]);

  useEffect(() => {
    if (brandList && brandList.length > 0) {
      const brandWithWebsite = brandList.find((brand) => brand.website);
      if (brandWithWebsite) {
        setValue("websiteUrl", brandWithWebsite.website, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [brandList, setValue]);

  const handleUpdateWebsite = () => {
    const brandToUpdateId = brandList?.[0]?.id;

    if (!brandToUpdateId) {
      console.error(
        "Cannot update website: brandId is missing from brandList.",
      );
      return;
    }

    let newWebsite = dialogWebsiteValue;
    if (
      dialogWebsiteValue &&
      hasValidDomain(dialogWebsiteValue) &&
      !isValidUrl(dialogWebsiteValue)
    ) {
      newWebsite = "https://" + dialogWebsiteValue;
    }

    if (websiteUrl !== newWebsite) {
      dispatch(setIsWebsiteChanged(true));

      const formDataForApi = new FormData();
      formDataForApi.append("website", newWebsite);

      dispatch(
        editBrandById({ id: brandToUpdateId.toString(), body: formDataForApi }),
      ).finally(() => {
        setValue("websiteUrl", newWebsite, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setOpenEditWebsite(false);
      });
    } else {
      setOpenEditWebsite(false);
    }
  };

  return (
    <div>
      <RevCard title="Share your website" className="!px-4 !pb-4">
        <div className="relative w-full">
          <RevInput
            name="website"
            label="URL"
            type="text"
            size="md"
            required
            register={register("websiteUrl", {
              required: "Brand website is required.",
              pattern: {
                value: domainRegex,
                message: "Invalid website",
              },
            })}
            error={errors.websiteUrl}
            disabled={true}
          />
        </div>
      </RevCard>

      <RevDialog
        isOpen={isOpenEditWebsite}
        title="Edit Website"
        setIsOpen={() => setOpenEditWebsite(false)}
        btnList={[
          <RevButton
            key="onboarding-cancel-website-edit"
            text="Cancel"
            variant="secondary"
            handleClick={() => setOpenEditWebsite(false)}
          />,
          <RevButton
            key="onboarding-save-website-edit"
            text="Update Website"
            variant="primary"
            handleClick={handleUpdateWebsite}
            disabled={!hasValidDomain(dialogWebsiteValue)}
          />,
        ]}
      >
        <div className="space-y-2.5 w-full">
          <RevAlert
            data={{
              heading: "Warning",
              text: "This change updates critical data that feeds our AI platform and will result in additional processing time.",
              type: "warning",
              id: "edit-website-id",
              show: true,
            }}
          />
          <RevInput
            name="websiteDialog"
            label="Brand website"
            type="url"
            size="md"
            required
            value={dialogWebsiteValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDialogWebsiteValue(e.target.value)
            }
          />
        </div>
      </RevDialog>
    </div>
  );
};
